/**
 * Day-of-week squircle pill row for relay schedule cards.
 *
 * Small shared UI piece used by both ScheduleList (text-based view) and
 * ScheduleTimeline (24 h bar view) to show which days of the week a
 * schedule is active.
 *
 * Contains:
 * - buildDayPillBooleans — decodes a days_of_week bitmask into a
 *   [Sun, Mon, …, Sat] boolean array
 * - DayPills — renders the row of coloured squircle chips
 */
import { Box, Typography } from '@mui/material';
import { t } from 'i18next';
import React from 'react';

import { daysOfWeekToBooleans } from '@sophocles/entities/RelaySchedule/relayScheduleUtils';

// Day-of-week labels in Sun→Sat display order.
const DOW_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// Common day patterns in Sun→Sat order, collapsed to a word instead of pills.
const WEEKDAYS = [false, true, true, true, true, true, false];
const WEEKENDS = [true, false, false, false, false, false, true];

const sameDays = (a, b) => a.length === b.length && a.every((value, i) => value === b[i]);

/**
 * Collapses an active-days boolean array to a human label for the common
 * cases (every day / weekdays / weekends). Returns null for custom
 * selections, which fall back to the pill row.
 *
 * @param {boolean[]} days [Sun, Mon, …, Sat]
 * @returns {string|null}
 */
function describeDays(days) {
  if (days.every(Boolean)) {
    return t('schedule.everyDay', 'Every day');
  }
  if (sameDays(days, WEEKDAYS)) {
    return t('schedule.weekdays', 'Weekdays');
  }
  if (sameDays(days, WEEKENDS)) {
    return t('schedule.weekends', 'Weekends');
  }
  return null;
}

/**
 * Converts a days_of_week bitmask into a 7-boolean array ordered Sun→Sat for
 * the squircle pill row. Reuses the canonical Mon→Sun decode in
 * relayScheduleUtils and just reorders it into pill (Sun-first) layout.
 *
 * @param {number|null} daysOfWeek
 * @returns {boolean[]} 7-element array [Sun, Mon, Tue, Wed, Thu, Fri, Sat]
 */
export function buildDayPillBooleans(daysOfWeek) {
  const monSpaceActive = daysOfWeekToBooleans(daysOfWeek); // [Mon, Tue, …, Sun]
  return [
    monSpaceActive[6], // Sun
    monSpaceActive[0], // Mon
    monSpaceActive[1], // Tue
    monSpaceActive[2], // Wed
    monSpaceActive[3], // Thu
    monSpaceActive[4], // Fri
    monSpaceActive[5], // Sat
  ];
}

/**
 * Day-of-week display for a schedule. Common patterns (every day,
 * weekdays, weekends) collapse to a single muted word; custom selections
 * render as a row of seven squircle pills coloured by active state.
 *
 * @param {{ days: boolean[] }} props
 */
export function DayPills({ days }) {
  const label = describeDays(days);
  // if (label) {
  //   return (
  //     <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
  //       {label}
  //     </Typography>
  //   );
  // }

  return (
    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
      {DOW_LABELS.map((label, i) => {
        const active = days[i];
        return (
          <Box
            key={i}
            sx={{
              width: 18,
              height: 18,
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.625rem',
              fontWeight: 700,
              lineHeight: 1,
              bgcolor: active ? 'primary.main' : 'transparent',
              border: active ? 'none' : '1px solid',
              borderColor: active ? 'transparent' : 'divider',
              color: active ? 'primary.contrastText' : 'text.disabled',
            }}
          >
            {label}
          </Box>
        );
      })}
    </Box>
  );
}
